/**
 * The rule for rendered audio against its placeholders (owner, Sep 25: "I still sometimes hear the synth songs and browser tts
 * voices. They really should not be playing when there is a rendered one available (I'd rather have a small delay than song
 * starting as synth and then continuing as real one)").
 *
 * Every radio path asks here — the stations' talk and songs (main.ts stationStep / playSegment), Community FM, the Loops
 * playlist, the street's voices (chatter.ts):
 *
 * * the item has a file in the server's index → play the file. While it is still downloading or decoding: wait in silence.
 *   A file that failed to load is skipped (silence) — never the browser's voice or the synth instead;
 * * the index itself has not arrived yet → wait (the first second after joining: a short silence);
 * * the item has no file, and the world has rendered audio of that kind → skip it. The stations pick the next item that is
 *   rendered in its place (sprawlfm.ts `pickVoiced`, the rendered rotation); what cannot be swapped (a headline about a
 *   real happening, a sponsor's read on a community show) is left out;
 * * nothing of that kind is rendered at all (a fresh server, a local dev setup, single player) → the placeholders, as before:
 *   the browser's voice and the synthesized music.
 *
 * The skip is the world's choice (owner, Sep 25 — Q100: "default skip is ok. This might be good to be as a server setting"): the admin
 * panel's rule *radioFallback* (GameRules, on the wire as NetOpts.radioFallback, live) plays an item with no file in the placeholders
 * instead, and the stations stop swapping in the next rendered item. A file that exists is waited for either way.
 */
/** what the server has rendered: undefined while its index is on the way, else whether it holds any voice lines / any songs; `fallback`: the
 *  world's setting (GameRules.radioFallback, owner, Sep 25 — Q100) — what has no recording plays in the placeholders instead of being skipped */
export interface Rendered { lines?: boolean; songs?: boolean; fallback?: boolean }
/** single player and a page with no game server behind it: nothing is rendered, the placeholders play */
export const NOTHING_RENDERED: Rendered = { lines: false, songs: false };
/** play the file, wait for it in silence, skip the item in silence, or play the placeholder (the browser's voice / the synth) */
export type RadioChoice = 'play' | 'wait' | 'skip' | 'fallback';
/** where a file is in the audio engine's cache (engine/audio.ts `fileState`) */
export type FileState = 'ready' | 'loading' | 'failed';

/** a spoken line: `has` = its file is in the index; `known` = the index's answer (undefined: not arrived yet); `fallback` = the world's
 *  radioFallback setting — an item without a file on a server with recordings plays in the placeholder instead of being skipped */
export function lineChoice(has: boolean, known: boolean | undefined, fallback = false): RadioChoice {
  if (has) return 'play';
  if (known === undefined) return 'wait';
  return known && !fallback ? 'skip' : 'fallback';
}
/** a song or a bed: as a line, and a file that is in the index is played only once it is decoded — until then the radio is quiet
 *  (whatever the setting: a recording never starts as its placeholder) */
export function trackChoice(has: boolean, known: boolean | undefined, file?: FileState, fallback = false): RadioChoice {
  if (has) return file === 'ready' ? 'play' : file === 'failed' ? 'skip' : 'wait';
  return lineChoice(false, known, fallback);
}
/** "skip it and play the next rendered item": the first of `list` from `start` on (walking round) that passes `ok`; none → undefined */
export function nextRendered<T>(list: readonly T[], start: number, ok: (t: T) => boolean): T | undefined {
  const n = list.length; if (!n) return undefined;
  const s = ((Math.floor(start) % n) + n) % n;
  for (let i = 0; i < n; i++) { const t = list[(s + i) % n]; if (ok(t)) return t; }
  return undefined;
}
